export interface CvEducation {
  status: string
  level?: string | null
  faculty_id?: number | null
  faculty_other?: string | null
  university?: string | null
  start_date?: string | null
  end_date?: string | null
  is_current?: boolean
}

export interface CvCourse {
  name: string
  provider?: string | null
  details?: string | null
}

export interface CvExperience {
  title: string
  company: string
  start_date?: string | null
  end_date?: string | null
  is_current?: boolean
  description?: string | null
}

export interface CvSkill {
  name: string
  level?: string | null
}

export interface CvLanguage {
  language_id?: number | null
  language_other?: string | null
  level?: string | null
}

export interface CvPersonal {
  full_name?: string | null
  photo_url?: string | null
  email?: string | null
  whatsapp?: string | null
  dob?: string | null
  country_id?: number | null
  state_id?: number | null
  city_id?: number | null
  city_text?: string | null
  address_details?: string | null
  website?: string | null
  linkedin?: string | null
  github?: string | null
  sex?: string | null
}

export interface CvPreferences {
  pref_location?: string | null
  pref_work_type?: string | null
  preferred_job_ids?: number[]
}

export const DEFAULT_CV_PREFERENCES: CvPreferences = {
  pref_location: 'both',
  pref_work_type: 'both',
  preferred_job_ids: [],
}

export function calculateTotalExperienceYears(entries: CvExperience[]): number {
  function parseMonth(value?: string | null): number | null {
    if (!value || value === 'present' || value === 'Present') {
      const now = new Date()
      return now.getFullYear() * 12 + now.getMonth()
    }
    const parts = String(value).split('-')
    const year = Number(parts[0])
    const month = Number(parts[1])
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null
    return year * 12 + (month - 1)
  }

  function interval(entry: CvExperience) {
    const start = parseMonth(entry.start_date)
    if (start == null) return null
    let end = parseMonth(entry.is_current ? 'present' : entry.end_date)
    if (end == null) end = parseMonth('present')
    if (end < start) return null
    return { start, end: end + 1 }
  }

  const intervals = entries.map(interval).filter(Boolean) as { start: number, end: number }[]
  if (!intervals.length) return 0

  const sorted = [...intervals].sort((a, b) => a.start - b.start)
  const merged = [{ ...sorted[0] }]
  for (let i = 1; i < sorted.length; i++) {
    const prev = merged[merged.length - 1]
    const cur = sorted[i]
    if (cur.start <= prev.end) prev.end = Math.max(prev.end, cur.end)
    else merged.push({ ...cur })
  }

  const totalMonths = merged.reduce((sum, iv) => sum + (iv.end - iv.start), 0)
  return Math.round((totalMonths / 12) * 10) / 10
}

import { getDefaultCountryId } from '~/data/geography'
import { normalizeCvEducations } from '~/utils/cv-validation'
import { normalizeOptionalUrl } from '~/utils/url'

/** Prevents overlapping /api/profile fetches from useAsyncData + load(). */
let profileFetchInflight: Promise<void> | null = null

/** Drop cached CV state so the next load hits /api/profile once. */
export function resetCvProfileCache() {
  profileFetchInflight = null
  useState('cv-loaded', () => false).value = false
  clearNuxtData('cv-profile')
}

export function useCvEditor() {
  const { get, save, uploadPhoto, removePhoto } = useProfile()
  const { loadSession, isAuthenticated } = useAuth()
  const saving = ref(false)
  const loaded = useState('cv-loaded', () => false)

  const personal = useState<CvPersonal>('cv-personal', () => ({}))
  const summary = useState('cv-summary', () => '')
  const preferences = useState<CvPreferences>('cv-preferences', () => ({ ...DEFAULT_CV_PREFERENCES }))
  const educations = useState<CvEducation[]>('cv-educations', () => [])
  const courses = useState<CvCourse[]>('cv-courses', () => [])
  const experiences = useState<CvExperience[]>('cv-experiences', () => [])
  const skills = useState<CvSkill[]>('cv-skills', () => [])
  const languages = useState<CvLanguage[]>('cv-languages', () => [])
  const lastSavedSummary = useState('cv-last-saved-summary', () => '')
  const lastSavedFullName = useState('cv-last-saved-full-name', () => '')

  const experienceYears = computed(() => calculateTotalExperienceYears(experiences.value))

  function applyProfileData(data: any) {
    personal.value = { ...(data.personal ?? {}) }
    summary.value = data.summary ?? ''
    preferences.value = {
      pref_location: data.preferences?.pref_location ?? DEFAULT_CV_PREFERENCES.pref_location ?? 'both',
      pref_work_type: data.preferences?.pref_work_type ?? DEFAULT_CV_PREFERENCES.pref_work_type ?? 'both',
      preferred_job_ids: (data.preferences?.preferred_jobs ?? []).map((job: { id: number }) => job.id),
    }
    educations.value = normalizeCvEducations(data.educations)
    courses.value = data.courses ?? []
    experiences.value = data.experiences ?? []
    skills.value = data.skills ?? []
    languages.value = data.languages ?? []
    lastSavedSummary.value = data.summary ?? ''
    lastSavedFullName.value = data.personal?.full_name ?? ''
    loaded.value = true
  }

  const { pending, refresh } = useAsyncData(
    'cv-profile',
    async () => {
      await loadSession()
      if (!isAuthenticated.value) {
        return null
      }

      const data = await get<any>()
      if (!data.personal) {
        data.personal = {}
      }
      if (!data.personal.country_id) {
        data.personal.country_id = await getDefaultCountryId()
      }
      applyProfileData(data)
      return data
    },
    { dedupe: 'defer' },
  )

  const loading = computed(() => pending.value)

  async function load() {
    if (loaded.value) {
      return
    }

    if (profileFetchInflight) {
      await profileFetchInflight
      return
    }

    if (pending.value) {
      await new Promise<void>((resolve) => {
        const stop = watch(pending, (isPending) => {
          if (!isPending) {
            stop()
            resolve()
          }
        })
        setTimeout(() => {
          stop()
          resolve()
        }, 8000)
      })
      return
    }

    profileFetchInflight = refresh().finally(() => {
      profileFetchInflight = null
    })
    await profileFetchInflight
  }

  async function reload() {
    loaded.value = false
    profileFetchInflight = null
    await refresh()
  }

  function normalizePersonalLinks() {
    personal.value = {
      ...personal.value,
      website: normalizeOptionalUrl(personal.value.website),
      linkedin: normalizeOptionalUrl(personal.value.linkedin),
      github: normalizeOptionalUrl(personal.value.github),
    }
  }

  function buildPayload() {
    return {
      personal: {
        ...personal.value,
        website: normalizeOptionalUrl(personal.value.website),
        linkedin: normalizeOptionalUrl(personal.value.linkedin),
        github: normalizeOptionalUrl(personal.value.github),
      },
      summary: summary.value,
      preferences: { ...preferences.value },
      educations: educations.value,
      courses: courses.value,
      experiences: experiences.value,
      skills: skills.value,
      languages: languages.value,
    }
  }

  async function persist() {
    // Full name is mandatory: it can be edited but never blanked. If it was
    // cleared, restore the last saved value before saving.
    if (!(personal.value.full_name ?? '').trim() && lastSavedFullName.value.trim()) {
      personal.value.full_name = lastSavedFullName.value
    }
    normalizePersonalLinks()
    saving.value = true
    try {
      const data = await save(buildPayload())
      if (data) {
        applyProfileData(data)
      }
      lastSavedSummary.value = summary.value ?? ''
      lastSavedFullName.value = personal.value.full_name ?? ''
    }
    finally {
      saving.value = false
    }
  }

  async function persistSummaryIfChanged(): Promise<boolean> {
    const current = (summary.value ?? '').trim()
    const saved = (lastSavedSummary.value ?? '').trim()
    if (current === saved) {
      return false
    }
    await persist()
    return true
  }

  async function persistFullNameIfChanged(): Promise<boolean> {
    const current = (personal.value.full_name ?? '').trim()
    const saved = (lastSavedFullName.value ?? '').trim()
    // Name is mandatory: if it was cleared, restore the last saved value
    // (the user may edit it, but never leave it empty).
    if (!current) {
      personal.value.full_name = lastSavedFullName.value
      return false
    }
    if (current === saved) {
      return false
    }
    await persist()
    return true
  }

  async function uploadProfilePhoto(file: File) {
    saving.value = true
    try {
      const data = await uploadPhoto<any>(file)
      if (data?.personal?.photo_url) {
        personal.value = { ...personal.value, photo_url: data.personal.photo_url }
      }
      return data
    }
    finally {
      saving.value = false
    }
  }

  async function deleteProfilePhoto() {
    saving.value = true
    try {
      await removePhoto()
      personal.value = { ...personal.value, photo_url: null }
    }
    finally {
      saving.value = false
    }
  }

  return {
    loading,
    saving,
    loaded,
    personal,
    summary,
    preferences,
    educations,
    courses,
    experiences,
    skills,
    languages,
    experienceYears,
    load,
    reload,
    persist,
    persistSummaryIfChanged,
    persistFullNameIfChanged,
    buildPayload,
    uploadProfilePhoto,
    deleteProfilePhoto,
  }
}
